import { t } from "@lingui/core/macro";
import { MobileSidebarToggle } from "@/Components/NextBase/MobileSidebarToggle";
import { useClientFeature } from "@/hooks/useClientFeature";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import React, { useState } from "react";
import styled from "styled-components";
import { useCalendarStore } from "../store/useCalendarStore";
import { LimeAutocomplete } from "@/Components/NextBase/LimeAutocomplete";
import { AutocompleteItem, Button, Link } from "@heroui/react";
import { Tabs } from "@heroui-v3/react";
import { CalendarModeToggle } from "./CalendarModeToggle";
import { Calendar, CalendarSync, MapPin, Timer } from "lucide-react";
import { Filter } from "./Filter";
import { Trans } from "@lingui/react/macro";
import { LimeDateTimeframePicker } from "@/Components/NextBase/LimeDateTimeFramePicker";
import { api } from "@/lib/api-client";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";

const Header = () => {
  const {
    dateActions,
    calendarView,
    selectedDate,
    locations,
    filterItems,
    locationActions,
    refreshCalendar,
    selectedAppointmentId,
  } = useCalendarStore(
    useShallow((state) => ({
      dateActions: state.dateActions,
      calendarView: state.calendarView,
      selectedDate: state.selectedDate,
      locations: state.locations,
      filterItems: state.filterItems,
      locationActions: state.locationActions,
      refreshCalendar: state.refreshCalendar,
      selectedAppointmentId: state.selectedAppointmentId,
    })),
  );

  // Control the date picker popover so we can force it closed when an
  // appointment drawer opens. Clicking an appointment block leaves its hover
  // Tooltip as the topmost entry in HeroUI's shared overlay stack, which makes
  // the picker's outside-press dismissal a no-op (it only closes the topmost
  // overlay). `selectedAppointmentId` flips non-null exactly when that drawer
  // opens — see CalendarModalWrapper's `isDrawerOpen`.
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Adjust state during render (instead of an effect) when the external store
  // value changes — the React-recommended pattern, avoids a cascading render.
  const [prevSelectedAppointmentId, setPrevSelectedAppointmentId] = useState(
    selectedAppointmentId,
  );
  if (selectedAppointmentId !== prevSelectedAppointmentId) {
    setPrevSelectedAppointmentId(selectedAppointmentId);
    if (selectedAppointmentId != null) {
      setIsDatePickerOpen(false);
    }
  }

  // Month whose holidays are currently fetched for the date-picker popover.
  // Defaults to the selected date's month and follows the user as they navigate
  // months inside the popover (via `onVisibleMonthChange`).
  const [pickerMonth, setPickerMonth] = useState(() =>
    (selectedDate ?? dayjs()).startOf("month"),
  );

  // Keep the fetched month in sync when the selected date jumps to a different
  // month from outside the picker (e.g. the "Danes" button or calendar nav).
  const selectedMonthKey = (selectedDate ?? dayjs()).format("YYYY-MM");
  const [prevSelectedMonthKey, setPrevSelectedMonthKey] =
    useState(selectedMonthKey);
  if (selectedMonthKey !== prevSelectedMonthKey) {
    setPrevSelectedMonthKey(selectedMonthKey);
    setPickerMonth((selectedDate ?? dayjs()).startOf("month"));
  }

  const selectedLocationId = filterItems.FK_locationId;
  const { data: holidaysData } = api.location.useGetLocationHolidays({
    locationId: selectedLocationId ?? -1,
    from: pickerMonth.startOf("month").format("YYYY-MM-DD"),
    to: pickerMonth.endOf("month").format("YYYY-MM-DD"),
    enabled: selectedLocationId != null,
  });

  const permissions = useUserPermissionsStore(
    useShallow((state) => state.permissions),
  );

  // Mirrors the sidebar's waitlist gate (`Sidebar.tsx:105`). Without it,
  // unflagged tenants saw a "Čakalna vrsta" button that linked to a
  // blank page — the route itself is feature-gated in DashboardRoutes,
  // so clicks dead-ended.
  const { isFeatureEnabled } = useClientFeature();
  const canViewWaitlist =
    isFeatureEnabled("waitlist") && Boolean(permissions.view_waitlist);

  const setLocation = (location: number) => {
    locationActions.setSelectedLocationId(location);
    refreshCalendar();
  };

  return (
    <Wrapper className="bg-white dark:bg-neutral-900">
      <div className={"left"}>
        <div
          className="relative flex flex-1 items-center justify-around gap-3"
          data-identifier="calendar-date-controls"
        >
          <Button
            variant="flat"
            onPress={() => dateActions.setSelectedDate(dayjs())}
            startContent={<Calendar size={16} />}
          >
            <Trans>Danes</Trans>
          </Button>

          <LimeDateTimeframePicker
            isOpen={isDatePickerOpen}
            onOpenChange={setIsDatePickerOpen}
            selectedDate={
              selectedDate ? parseDate(selectedDate.format("YYYY-MM-DD")) : null
            }
            onDateChange={(date) => {
              if (!date) return;

              dateActions.setSelectedDate(
                dayjs(date.toDate(getLocalTimeZone())),
              );
            }}
            calendarView={calendarView}
            holidays={holidaysData?.holidays}
            onVisibleMonthChange={(firstDayOfMonth) =>
              setPickerMonth(dayjs(firstDayOfMonth).startOf("month"))
            }
          />
        </div>
      </div>
      <div className={"right"}>
        {canViewWaitlist && (
          <Button
            variant="flat"
            as={Link}
            href="/dashboard/waitlist"
            startContent={<Timer size={16} />}
            className="hidden lg:flex"
          >
            <Trans>Čakalna vrsta</Trans>
          </Button>
        )}

        {isFeatureEnabled("fixedAppointments") && (
          <Button
            variant="flat"
            as={Link}
            href="/dashboard/fixed-appointments"
            startContent={<CalendarSync size={16} />}
            className="hidden lg:flex"
          >
            <Trans>Redni termini</Trans>
          </Button>
        )}

        <LimeAutocomplete
          isVirtualized={false} // TODO: set to true after bugs are fixed (https://github.com/heroui-inc/heroui/issues/4494) , (https://github.com/heroui-inc/heroui/issues/4568)
          selectedKey={filterItems.FK_locationId?.toString()}
          size={"sm"}
          placeholder={t`Lokacija`}
          className="hidden w-fit md:block"
          startContent={<MapPin size={16} className="shrink-0" />}
          isClearable={false}
          inputProps={{
            classNames: {
              inputWrapper: "h-[39px] text-default-500",
              input: "text-default-400",
            },
          }}
          scrollShadowProps={{
            isEnabled: false,
          }}
          onSelectionChange={(key) => {
            if (!key) return;

            setLocation(Number(key));
          }}
        >
          {locations?.map((location: { locationId: number; name: string }) => {
            return (
              <AutocompleteItem
                key={location.locationId.toString()}
                textValue={location.name}
              >
                {location.name}
              </AutocompleteItem>
            );
          })}
        </LimeAutocomplete>

        <MobileSidebarToggle withoutRightMargin />

        <Tabs
          data-identifier="calendar-view-tabs"
          selectedKey={calendarView ?? "week"}
          onSelectionChange={(key) =>
            dateActions.setCalendarView(key == null ? "week" : String(key))
          }
          variant="primary"
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label={t`Pogled koledarja`}>
              <Tabs.Tab
                id="month"
                className="px-3"
                aria-label={t`Mesec`}
                data-identifier="calendar-view-month"
              >
                <span className="hidden md:block">
                  <Trans>Mesec</Trans>
                </span>
                <span className="block md:hidden">
                  <Trans>M</Trans>
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab
                id="week"
                className="px-3"
                aria-label={t`Teden`}
                data-identifier="calendar-view-week"
              >
                <span className="hidden md:block">
                  <Trans>Teden</Trans>
                </span>
                <span className="block md:hidden">
                  <Trans>T</Trans>
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab
                id="day"
                className="px-3"
                aria-label={t`Dan`}
                data-identifier="calendar-view-day"
              >
                <span className="hidden md:block">
                  <Trans>Dan</Trans>
                </span>
                <span className="block md:hidden">
                  <Trans>D</Trans>
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>
          <Tabs.Panel id="month" className="m-0 hidden p-0">
            {null}
          </Tabs.Panel>
          <Tabs.Panel id="week" className="m-0 hidden p-0">
            {null}
          </Tabs.Panel>
          <Tabs.Panel id="day" className="m-0 hidden p-0">
            {null}
          </Tabs.Panel>
        </Tabs>

        <CalendarModeToggle />

        {permissions.restrict_calendar_filter ? null : <Filter></Filter>}
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  border-bottom: 1px solid #eaebed;
  padding-bottom: 5px;

  grid-column: 1 / -1;

  height: 55px;

  z-index: 6;

  @media (min-width: 769px) {
    position: sticky;
    top: var(--safe-area-top, 0px);
  }

  .left {
    display: flex;
  }

  .right {
    display: flex;
    gap: 10px;
  }

  .location-picker-desktop {
    display: flex;
    justify-content: start;
    gap: 10px;
    align-items: center;
    white-space: nowrap;

    color: #969ba9;

    font-size: 14px;

    padding: 6px 12px;
    border-radius: 4px;
    background: #fafafa;
  }
  .mobile-top-row {
    display: none;
    width: 100%;
    justify-content: flex-end;

    border-bottom: 1px solid #eaebed;
    padding-bottom: 20px;

    height: 50px;
  }
  .location-picker-mobile {
    display: none;
    width: fit-content;

    border-radius: 4px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    justify-content: center;
    align-items: unset;

    padding: 0 15px;

    padding-bottom: 0px;

    .left {
      order: 2;

      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 15px;
      /* Keep the date controls above the iOS home indicator. */
      padding-bottom: calc(15px + var(--safe-area-bottom, 0px));
      background: white;

      z-index: 5;

      border-top: 1px solid rgb(234, 235, 237);
    }

    .right {
      order: 1;

      justify-content: space-between;
    }

    .location-picker-desktop {
      display: none;
    }
    .mobile-top-row {
      display: flex;
    }
    .location-picker-mobile {
      display: flex;
    }
  }
`;

export default React.memo(Header);
